import { z } from "zod";

import { timestampSchema } from "~/modules/ai-edit-tool/schemas";
import { jerboaBrowserInfoSchema } from "~/modules/jerboa/schemas";

export const jerboaClientNetworkLoadStatusSchema = z.enum(["finish", "error"]);
export type JerboaClientNetworkLoadStatus = z.infer<typeof jerboaClientNetworkLoadStatusSchema>;

export const jerboaClientNetworkLoadContextSchema = z.enum(["preview", "export"]);
export type JerboaClientNetworkLoadContext = z.infer<typeof jerboaClientNetworkLoadContextSchema>;

const jerboaClientNetworkLoadEditTypeSchema = z.enum(["captions", "ai-edit"]);

const jerboaClientNetworkLoadAssetClassificationSchema = z.enum(["hit", "revalidated", "miss"]);
export type JerboaClientNetworkLoadAssetClassification = z.infer<
  typeof jerboaClientNetworkLoadAssetClassificationSchema
>;

const jerboaClientNetworkLoadAssetSchema = z.object({
  name: z.string(),
  initiatorType: z.string().nullish(),
  startMs: z.number(),
  endMs: z.number(),
  transferSize: z.number(),
  encodedBodySize: z.number(),
  decodedBodySize: z.number(),
  classification: jerboaClientNetworkLoadAssetClassificationSchema,
  nextHopProtocol: z.string().nullish(),
});

export type JerboaClientNetworkLoadAsset = z.infer<typeof jerboaClientNetworkLoadAssetSchema>;

const jerboaClientNetworkLoadSummarySchema = z.object({
  id: z.string(),
  sessionId: z.string(),
  videoId: z.string(),
  projectId: z.string(),
  captionsProjectId: z.string().nullish(),
  editType: jerboaClientNetworkLoadEditTypeSchema.nullish(),
  context: jerboaClientNetworkLoadContextSchema,
  status: jerboaClientNetworkLoadStatusSchema,
  totalDurationMs: z.number(),
  timeToFirstFrameMs: z.number().nullish(),
  assetCount: z.number(),
  assetCacheHitCount: z.number(),
  assetMissCount: z.number(),
  assetBytesFromNetwork: z.number(),
  assetBytesFromCache: z.number(),
  maxConcurrentFetches: z.number(),
  networkEffectiveType: z.string().nullish(),
  networkDownlinkMbps: z.number().nullish(),
  networkObservedMbps: z.number().nullish(),
  networkRttMs: z.number().nullish(),
  httpProtocolMajority: z.string().nullish(),
  appVersion: z.string().nullish(),
  createdAt: timestampSchema,
  userId: z.string().nullish(),
  orgId: z.string().nullish(),
});

const jerboaClientNetworkLoadDetailSchema = jerboaClientNetworkLoadSummarySchema.extend({
  wasmLoadDurationMs: z.number().nullish(),
  wasmInitDurationMs: z.number().nullish(),
  projectAssetCount: z.number().nullish(),
  errorMessage: z.string().nullish(),
  assetRevalidatedCount: z.number(),
  networkSaveData: z.boolean().nullish(),
  wasVisibleThroughout: z.boolean().nullish(),
  projectProtoUri: z.string().nullish(),
  assets: z.array(jerboaClientNetworkLoadAssetSchema).nullish(),
  browserInfo: jerboaBrowserInfoSchema.nullish(),
});

export const jerboaClientNetworkLoadsListResponseSchema = z.object({
  page: z.array(jerboaClientNetworkLoadSummarySchema),
  pageInfo: z.object({
    hasNextPage: z.boolean(),
    endCursor: z.string().nullable(),
  }),
});

export const jerboaClientNetworkLoadDetailResponseSchema = z.object({
  result: jerboaClientNetworkLoadDetailSchema,
});

const networkLoadAnalysisBucketSchema = z.object({
  bucketStart: z.number(),
  bucketEnd: z.number(),
  totalCount: z.number(),
  errorCount: z.number(),
  finishCount: z.number(),
  errorRate: z.number(),
  totalDurationMsMedian: z.number(),
  totalDurationMsP25: z.number(),
  totalDurationMsP75: z.number(),
  totalDurationMsP90: z.number().nullish(),
  totalDurationMsP95: z.number(),
  assetBytesFromNetworkMedian: z.number(),
  networkObservedMbpsMedian: z.number(),
  previewCount: z.number(),
  previewDurationMedian: z.number(),
  previewErrorRate: z.number(),
  exportCount: z.number(),
  exportDurationMedian: z.number(),
  exportErrorRate: z.number(),
  appVersions: z.array(z.string()).nullish(),
  appVersionCounts: z.record(z.string(), z.number()).nullish(),
  appPrimaryVersion: z.string().nullish(),
  appPrimaryVersionShare: z.number().nullish(),
  appVersionCoverage: z.number().nullish(),
  appVersionFirstSeenInBucket: z.record(z.string(), z.number()).nullish(),
  releaseMarkers: z.array(z.object({ version: z.string(), firstSeenAt: z.number() })).nullish(),
  appVersionFirstSeenMap: z.record(z.string(), z.number()).nullish(),
});

export type NetworkLoadAnalysisBucket = z.infer<typeof networkLoadAnalysisBucketSchema>;

export const networkLoadAnalysisResponseSchema = z.object({
  analysis: z.object({
    buckets: z.array(networkLoadAnalysisBucketSchema),
  }),
});
